import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Div, RowDiv, Text, Section, CenterRowDiv } from 'styles';
import { useSearchParams } from 'react-router-dom';
import { CustomDate } from 'components/datepicker/CustomDate';
import { SubTitle } from 'components';
import { TableChart } from 'components/timeTable/tableChart/TableChart';
import { Spinner } from 'components/loading/Spinner';
import { sortBy, groupBy } from 'lodash';
import { DISPLAY_VALUE_TYPES } from 'data/constants';
import { Button } from 'components/button/Button';

import commonSensorService from 'services/common-sensor-service';
import containerCorridorService from 'services/container/container-corridor-service';

export function ConPower(props) {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [sensor, setSensor] = useState({});
  const [sensorUnit, setSensorUnit] = useState(null);
  const [sensorData, setSensorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartInputData, setChartInputData] = useState(null);
  const [chartOverrideOptions, setChartOverrideOptions] = useState({
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        intersect: false,
        external: function (context) {
          const tooltipModel = context.tooltip;
          let tooltipEl = document.getElementById('custom-tooltip');
  
          if (!tooltipModel.title || tooltipModel.title[0] === undefined) {
            if (tooltipEl) tooltipEl.style.opacity = 0;
            return;
          }
  
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'custom-tooltip';
            document.body.appendChild(tooltipEl);
          }
  
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }
  
          const position = context.chart.canvas.getBoundingClientRect();
          const dataPointY = tooltipModel.dataPoints[0].element.y;
  
          const tooltipX = position.left + window.scrollX + tooltipModel.caretX - 60;
          const tooltipY = position.top + window.scrollY + dataPointY - tooltipEl.offsetHeight - 20;
  
          tooltipEl.style.opacity = 1;
          tooltipEl.style.left = tooltipX + 'px';
          tooltipEl.style.top = tooltipY + 'px';
  
          const time = tooltipModel.title[0];
  
          let innerHtml = `<div class="row"><p class="title">날짜</p><p class="val">${time}</p></div>`;
          tooltipModel.dataPoints.forEach((dataPoint) => {
            const label = dataPoint.dataset.label || '';
            const value = dataPoint.formattedValue || '';
            innerHtml += `<div class="row"><p class="title">${label}</p><p class="val">${value}</p></div>`;
          });
  
          tooltipEl.innerHTML = innerHtml;
        },
      }
    }
  });

  const [filters, setFilters] = useState({
    startDate: moment().subtract(7, 'day').format('YYYY.MM.DD'),
    endDate: moment().format('YYYY.MM.DD'),
    type: DISPLAY_VALUE_TYPES[0].val,
    apiCallIndex: 0
  });

  const onChange = (name, val) => {
    setFilters({ ...filters, [name]: val });
  };

  const processDailyData = (data) => {

    const groupByDateData = groupBy(data, (d) => (moment(d.timestamp).format('YYYY-MM-DD')));
    let processedData = [];

    for (const [key, value] of Object.entries(groupByDateData)) {
      const entries = sortBy(value, 'timestamp');
  
      const time = key;
      let highest = 0;
      let lowest = null;
      let sum = 0;
      let count = 0;
      let average = 0;
      let lastValue = 0;
  
      entries.forEach((entry) => {
        const sensorValue = entry.sensor_value;
        
        highest = Math.max(highest, sensorValue);
        if (lowest === null) {
          lowest = sensorValue;
        } else {
          lowest = Math.min(lowest, sensorValue);
        }
        sum += sensorValue;
        
        count++

        if (count === entries.length) {
          lastValue = sensorValue;
        }
      });
  
      if (count > 0) {
        average = Math.round(sum/count*100)/100;
      }
  
      processedData.push({
        time,
        highest,
        lowest,
        sum,
        count,
        average,
        lastValue
      });
    }
  
    processedData = sortBy(processedData, 'time');

    return processedData;
  }

  const updateChartInputData = (data, chartDisplayType, pType) => {

    const dailyData = processDailyData(data);
    let strType ='';
    if(pType === null){
      strType = 'INT';
    }else{
      const [, strType1] = pType.split('_');
      strType = strType1;
    }
    
    let labels = [];
    let lastValueData = [];

    if(strType ==='INT'){           // 적산
      if (dailyData) {
        labels = dailyData.map((d) => moment(d.time).format('YYYY-MM-DD'));
        lastValueData = dailyData.map((d) => d.lastValue);
      }
    } else if(strType === 'INS'){      // 순시
      if (dailyData) {
        labels = dailyData.map((d) => moment(d.time).format('YYYY-MM-DD'));
        lastValueData = dailyData.map((d) => d.average);
      }
    }
    
  
    const newChartInputData = { 
      labels,
      data1: {
        name: '전력 소비량',
        color: '#AEE3E5',
        data: lastValueData
      }
    };

    setChartInputData(newChartInputData);
  }

  useEffect(() => {
    const fetchData = async () => {
      const startTime = moment(filters.startDate).format('YYYYMMDD000000');
      const endTime = moment(filters.endDate).format('YYYYMMDD235959');

      // 날짜 차이를 계산
      const monthsDiff = moment(filters.endDate).diff(moment(filters.startDate), 'months');
      
      // 6개월 이상이면 alert 창 표시
      if (monthsDiff > 6) {
        alert('6개월 이상은 조회 할 수 없습니다. \n값을 다시 설정해 주세요');
        setLoading(false);
        return; // 데이터 요청을 중단
      }

      let data = [];
      try {
        data = await containerCorridorService.getPowermeterSensorDetail(searchParams.get('type'), startTime, endTime);

        // data.forEach((d) => {
        //   d.sensor_value = Math.random()*1000;
        // })
      } catch (e) {
        console.error(e);
      }

      if (data && data.length > 0) {
        setSensor(data[0]);
        setSensorUnit(commonSensorService.getSensorUnit(data[0].sensor_type))
        setSensorData(data);
      } else {
        setSensor({});
        setSensorUnit(null);
        setSensorData([])
      }

      setLoading(false)
    }

    setLoading(true);
    fetchData();
  }, [searchParams, filters.apiCallIndex,]);

  useEffect(() => {
    console.log('컨테이너 센서데이터!!',sensorData)
    updateChartInputData(sensorData, filters.type, searchParams.get('type') )
  }, [sensorData, filters.type])

  const renderSpinner = () => (
    <CenterRowDiv $justifyContent="center" $padding="10px">
      <Spinner $width={50} $height={50}/>
    </CenterRowDiv>
  )

  return (
    <Section>
      {/* <SubTitle title="전력 소비량" sub="Corridor / Poweremeter" desc={sensor.sensor_name} /> */}
      <SubTitle title="전력 소비량" sub="Corridor / Poweremeter" desc={sensor.sensor_name === '조작전원 적산'? '조작전원 순시': sensor.sensor_name === '조작전원 순시'? '조작전원 적산': sensor.sensor_name } />
      {/* 조작전원의 경우 적산과 순시의 값이 뒤바껴서 조화하므로 오는 센서이름도 바껴서 표현되어 부제를 뒤바꿔 줘야함 250109 EJpark */}
      <Div $padding="32px 0">
        <Text $type="b" $color="assistive" $marginBottom={4}>
          기간선택
        </Text>
        <RowDiv $justifyContent="space-between">
          <RowDiv $gap={8}>
            <CustomDate name="startDate" filters={filters} onChange={onChange} desc="From" hide />
            <CustomDate name="endDate" filters={filters} onChange={onChange} desc="To" hide />

            <Button $type="standard" $size="small" onClick={() => {
              onChange('apiCallIndex', filters.apiCallIndex + 1)
            }}>
              검색
            </Button>
          </RowDiv>
          {/* <Switch name="timeType" filters={filters} onChange={onChange} list={switchList} /> */}
        </RowDiv>
        <Div $paddingTop={32}>
          { loading ?
            renderSpinner()
            :
            <TableChart filters={filters} chartInputData={chartInputData} chartOverrideOptions={chartOverrideOptions} unit={sensorUnit}/>
          }
        </Div>
      </Div>
    </Section>
  );
}
